/**
 * Sales user's client ledger (F1 redesign).
 *
 * Mirrors the columns + visual feel of /admin/payment-ledger but scoped
 * to the signed-in user's own submissions. Every number on screen comes
 * from server-side rollups (sub.totalReceived / totalRefunded / balance
 * Amount / receivedPercent) — no client-side math, no drift.
 *
 * Columns (sortable):
 *   Submitted · Client · Stage · Deal · Final payable · GST · TDS
 *   · Collected · Balance · % collected · Status · Aging · View
 *
 * Filters above the table: free-text search, stage chip, status chip.
 * Top tile shows portfolio rollup (totals + collection rate).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Plus, Eye, ArrowRight, Search, Filter, ArrowUpDown,
    Wallet, BarChart3, CheckCircle2, Clock, RefreshCw,
} from 'lucide-react';
import api from '../../utils/api';

/* ───────────────── helpers ──────────────────────────────────────────── */
const inr = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
        .format(Number(v || 0));
const fd  = (d) => {
    if (!d) return '—';
    const p = new Date(d);
    return isNaN(p) ? '—' : p.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const daysSince = (d) => {
    if (!d) return 0;
    const ms = Date.now() - new Date(d).getTime();
    return Math.max(0, Math.floor(ms / 86_400_000));
};

const STAGE_CHIP = {
    token:     'bg-amber-100 text-amber-800',
    loi:       'bg-sky-100 text-sky-800',
    agreement: 'bg-violet-100 text-violet-800',
    paid:      'bg-emerald-100 text-emerald-800',
    refunded:  'bg-rose-100 text-rose-800',
};
const STATUS_CHIP = {
    pending:   'bg-amber-50 text-amber-800 ring-1 ring-amber-200/80',
    active:    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80',
    on_hold:   'bg-amber-50 text-amber-800 ring-1 ring-amber-200/80',
    cancelled: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200/80',
    refunded:  'bg-rose-100 text-rose-800 ring-1 ring-rose-200/80',
    rejected:  'bg-rose-50 text-rose-700 ring-1 ring-rose-200/80',
};
const STAGE_FILTERS  = ['all', 'token', 'loi', 'agreement', 'paid', 'refunded'];
const STATUS_FILTERS = ['all', 'pending', 'active', 'on_hold', 'cancelled', 'refunded', 'rejected'];

function approvalStatusLabel(s) {
    if (s.status === 'submitted') return 'pending';
    if (s.status === 'rejected') return 'rejected';
    return s.clientStatus || 'active';
}

function matchesStatusFilter(s, statusFilter) {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return s.status === 'submitted';
    if (statusFilter === 'rejected') return s.status === 'rejected';
    return s.status === 'approved' && (s.clientStatus || 'active') === statusFilter;
}

function isRegisteredClient(s) {
    return s.status === 'approved';
}

/* ───────────────── component ────────────────────────────────────────── */
export default function ClientList() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ]            = useState('');
    const [stage, setStage]    = useState('all');
    const [status, setStatus]  = useState('all');
    const [sortBy, setSortBy]  = useState({ key: 'createdAt', dir: 'desc' });

    const load = () => {
        setLoading(true);
        api.get('/forms/submissions/me')
            .then(r => setItems(r.data.items || []))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    /* ── filtered + sorted view ── */
    const view = useMemo(() => {
        const rx = q.trim() ? new RegExp(q.trim(), 'i') : null;
        const list = items.filter(s => {
            if (stage  !== 'all' && (s.currentStage || 'token') !== stage)       return false;
            if (!matchesStatusFilter(s, status)) return false;
            if (rx) {
                const blob = `${s.data?.name || ''} ${s.data?.phone || ''} ${s.data?.email || ''} ${s._id || ''}`;
                if (!rx.test(blob)) return false;
            }
            return true;
        });
        const dir = sortBy.dir === 'asc' ? 1 : -1;
        const valOf = (s) => {
            switch (sortBy.key) {
                case 'name':           return (s.data?.name || '').toLowerCase();
                case 'deal':           return Number(s.data?.dealAmount || 0);
                case 'collected':      return Number(s.totalReceived || 0);
                case 'balance':        return Number(s.balanceAmount || 0);
                case 'receivedPercent':return Number(s.receivedPercent || 0);
                case 'aging':          return daysSince(s.createdAt);
                case 'createdAt':
                default:               return new Date(s.createdAt || 0).getTime();
            }
        };
        return [...list].sort((a, b) => {
            const va = valOf(a), vb = valOf(b);
            if (va < vb) return -1 * dir;
            if (va > vb) return  1 * dir;
            return 0;
        });
    }, [items, q, stage, status, sortBy]);

    /* ── portfolio rollups (approved bookings only) ── */
    const totals = useMemo(() => {
        const registered = view.filter(isRegisteredClient);
        const r = {
            deal: 0, payable: 0, collected: 0, balance: 0, refunded: 0,
            count: registered.length,
            pending: view.filter(s => s.status === 'submitted').length,
        };
        registered.forEach(s => {
            r.deal      += Number(s.data?.dealAmount || 0);
            r.payable   += Number(s.data?.finalPayableAmount || s.data?.dealAmount || 0);
            r.collected += Number(s.totalReceived || 0);
            r.balance   += Number(s.balanceAmount || 0);
            r.refunded  += Number(s.totalRefunded || 0);
        });
        r.colRate = r.payable > 0 ? (r.collected / r.payable) * 100 : 0;
        return r;
    }, [view]);

    const setSort = (key) => setSortBy(s =>
        s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-ink-900">My clients</h1>
                    <p className="text-sm text-ink-500 mt-1">
                        Server-side ledger view of every client you've submitted or are assigned to
                        on the sales team. All amounts are computed on the backend — what you see
                        here is the source of truth.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className="ag-btn-ghost" title="Refresh">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    <Link to="/sales/clients/new" className="ag-btn-primary inline-flex justify-center">
                        <Plus className="h-4 w-4" /> New client
                    </Link>
                </div>
            </div>

            {/* Portfolio rollup tile */}
            <div className="ag-card p-5">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <Mini icon={Wallet}      label="Registered clients" value={totals.count} />
                    <Mini icon={BarChart3}   label="Deal value"         value={inr(totals.deal)} />
                    <Mini icon={CheckCircle2} label="Collected"         value={inr(totals.collected)} />
                    <Mini icon={Clock}       label="Balance due"        value={inr(totals.balance)} />
                    <Mini icon={ArrowUpDown} label="Collection rate"    value={`${totals.colRate.toFixed(1)}%`} />
                </div>
            </div>

            {/* Filter bar */}
            <div className="ag-card p-3 flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search className="h-4 w-4 text-ink-500" />
                    <input
                        className="ag-input flex-1"
                        placeholder="Search client name, phone, email, ID…"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                    />
                </div>
                <FilterRow label="Stage"  value={stage}  options={STAGE_FILTERS}  onChange={setStage}  />
                <FilterRow label="Status" value={status} options={STATUS_FILTERS} onChange={setStatus} />
                <span className="text-xs text-ink-500 ml-auto">{view.length} of {items.length}</span>
            </div>

            {/* The mini-ledger */}
            <div className="rounded-2xl border border-ink-200/90 bg-white shadow-[0_4px_24px_rgba(15,23,42,0.06)] overflow-hidden ring-1 ring-ink-100/80">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[1100px]">
                        <thead>
                            <tr className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-left text-slate-300">
                                <Th label="Submitted"      onClick={() => setSort('createdAt')}       sort={sortBy.key === 'createdAt'        ? sortBy.dir : null} />
                                <Th label="Client"         onClick={() => setSort('name')}            sort={sortBy.key === 'name'             ? sortBy.dir : null} />
                                <Th label="Stage" />
                                <Th label="Deal"           onClick={() => setSort('deal')}            sort={sortBy.key === 'deal'             ? sortBy.dir : null} align="right" />
                                <Th label="Final payable"  align="right" />
                                <Th label="GST"            align="right" />
                                <Th label="TDS"            align="right" />
                                <Th label="Collected"      onClick={() => setSort('collected')}       sort={sortBy.key === 'collected'        ? sortBy.dir : null} align="right" />
                                <Th label="Balance"        onClick={() => setSort('balance')}         sort={sortBy.key === 'balance'          ? sortBy.dir : null} align="right" />
                                <Th label="%"              onClick={() => setSort('receivedPercent')} sort={sortBy.key === 'receivedPercent'  ? sortBy.dir : null} align="right" />
                                <Th label="Status" />
                                <Th label="Aging"          onClick={() => setSort('aging')}           sort={sortBy.key === 'aging'            ? sortBy.dir : null} align="right" />
                                <th className="px-4 py-3.5 text-right text-[10px] font-bold uppercase tracking-wider"> </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-100">
                            {view.map((s) => <LedgerRow key={s._id} s={s} />)}
                            {view.length === 0 && (
                                <tr><td colSpan={13} className="text-center text-ink-500 py-14 text-sm">
                                    {loading ? 'Loading…' : 'No clients match those filters.'}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

/* ───────────────── sub-components ───────────────────────────────────── */
function LedgerRow({ s }) {
    const stage   = s.currentStage || 'token';
    const status  = approvalStatusLabel(s);
    const registered = isRegisteredClient(s);
    const dealAmt = Number(s.data?.dealAmount || 0);
    const payable = Number(s.data?.finalPayableAmount || s.data?.dealAmount || 0);
    const gst     = Number(s.data?.gstAmount || 0);
    const tds     = Number(s.data?.tdsAmount || 0);
    const aging   = daysSince(s.createdAt);
    const agingCls = aging >= 60 ? 'text-rose-700 font-bold'
                   : aging >= 30 ? 'text-amber-700'
                   :               'text-ink-500';

    return (
        <tr className="group odd:bg-white even:bg-slate-50/70 hover:bg-brand-teal/[0.06] transition-colors">
            <Td>{fd(s.createdAt)}</Td>
            <Td>
                <div className="font-medium text-ink-900 truncate max-w-[180px]">{s.data?.name || '—'}</div>
                <div className="text-[11px] text-ink-500 font-mono tabular-nums">{s.data?.phone || ''}</div>
            </Td>
            <Td>
                <span className={`ag-chip ${STAGE_CHIP[stage] || 'bg-ink-100 text-ink-700'}`}>
                    {stage.toUpperCase()}
                </span>
            </Td>
            <Td align="right" mono className={registered ? '' : 'text-ink-400'}>{inr(dealAmt)}</Td>
            <Td align="right" mono className={registered ? '' : 'text-ink-400'}>{inr(payable)}</Td>
            <Td align="right" mono className="text-ink-600">{registered && gst ? inr(gst) : '—'}</Td>
            <Td align="right" mono className="text-ink-600">{registered && tds ? inr(tds) : '—'}</Td>
            <Td align="right" mono className="font-semibold text-emerald-700">
                {registered ? inr(s.totalReceived || 0) : '—'}
            </Td>
            <Td align="right" mono className={registered && Number(s.balanceAmount || 0) > 0 ? 'font-semibold text-ink-900' : 'text-ink-400'}>
                {registered ? inr(s.balanceAmount || 0) : '—'}
            </Td>
            <Td align="right" mono>
                {registered ? <PctBar value={Number(s.receivedPercent || 0)} /> : <span className="text-xs text-ink-400">—</span>}
            </Td>
            <Td>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CHIP[status] || STATUS_CHIP.active}`}>
                    {status === 'pending' ? 'pending approval' : status.replace('_', ' ')}
                </span>
            </Td>
            <Td align="right" className={`text-xs tabular-nums whitespace-nowrap ${agingCls}`}>{aging}d</Td>
            <Td align="right">
                <Link to={`/sales/clients/${s._id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-teal-700 hover:text-brand-teal-900">
                    <Eye className="h-3.5 w-3.5" /> View <ArrowRight className="h-3 w-3 opacity-60" />
                </Link>
            </Td>
        </tr>
    );
}

function PctBar({ value }) {
    const v = Math.max(0, Math.min(100, Number(value) || 0));
    return (
        <div className="inline-flex flex-col items-end gap-1 min-w-[80px]">
            <span className="text-xs font-semibold tabular-nums">{v.toFixed(1)}%</span>
            <div className="h-1.5 w-20 rounded-full bg-ink-100 overflow-hidden">
                <div
                    className={`h-full rounded-full ${
                        v >= 100 ? 'bg-emerald-500'
                      : v >= 60  ? 'bg-brand-teal'
                      : v >= 30  ? 'bg-amber-500'
                      :            'bg-rose-400'}`}
                    style={{ width: `${v}%` }}
                />
            </div>
        </div>
    );
}

function Th({ label, onClick, sort, align = 'left' }) {
    const interactive = !!onClick;
    return (
        <th
            onClick={onClick}
            className={`px-4 py-3.5 text-[10px] font-bold uppercase tracking-wider text-slate-400
                ${align === 'right' ? 'text-right' : ''}
                ${interactive ? 'cursor-pointer hover:text-white select-none' : ''}`}
        >
            <span className="inline-flex items-center gap-1">
                {label}
                {interactive && (
                    <ArrowUpDown className={`h-3 w-3 ${sort ? 'text-white' : 'opacity-40'}`} />
                )}
            </span>
        </th>
    );
}

function Td({ children, align = 'left', mono = false, className = '' }) {
    return (
        <td className={`px-4 py-3 ${align === 'right' ? 'text-right' : ''} ${mono ? 'tabular-nums' : ''} ${className}`}>
            {children}
        </td>
    );
}

function Mini({ icon: Icon, label, value }) {
    return (
        <div className="rounded-lg bg-ink-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold inline-flex items-center gap-1">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <div className="text-lg font-extrabold mt-0.5 tabular-nums truncate">{value}</div>
        </div>
    );
}

function FilterRow({ label, value, options, onChange }) {
    return (
        <div className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-ink-500" />
            <span className="text-[11px] text-ink-500 font-semibold uppercase tracking-wider">{label}:</span>
            <div className="flex gap-1">
                {options.map(o => (
                    <button
                        key={o}
                        onClick={() => onChange(o)}
                        className={`text-[11px] px-2 py-1 rounded-full transition
                            ${value === o ? 'bg-brand-teal text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
                    >
                        {o === 'all' ? 'All' : o.replace('_', ' ')}
                    </button>
                ))}
            </div>
        </div>
    );
}
